import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	LeaderChallengeStatsAPIResponse,
	LeaderSkillBuilderService,
	LeaderUserService,
	User,
} from '@services/leader';
import {
	ALL_SKILL_BUILDERS,
	Group,
	GroupService,
	Organization,
	OrganizationService,
	SKILL_BUILDER_FILTERS,
	SkillBuilder,
	SkillBuilderService,
} from '@services/member';
import { LanguageService, MainKey, StorageService } from '@services/public';
import { Select, SelectKey } from '@util';
import { ChartConfiguration } from 'chart.js';
import { ExposedPromise, uuid } from 'jakapa-utilities-lite';
import { ReplaySubject, Subscription } from 'rxjs';

const PAGE_PATH = `pages.researcher.dashboard.trainDashboard`;

enum Chart {
	SKILL = 'skill',
	ACTIVITY = 'activity',
	GOAL = 'goal',
	IMPACT = 'impact',
}

const CHARTS: Array<Chart> = [
	Chart.SKILL,
	Chart.ACTIVITY,
	Chart.GOAL,
	Chart.IMPACT,
];

@Component({
	selector: 'app-leader-dashboard-train',
	templateUrl: './train.component.html',
	styleUrls: ['./train.component.scss'],
})
export class LeaderDashboardTrainComponent implements OnInit, OnDestroy {
	// Page filters.
	filters = {
		organization: new Select<Organization>(),
		group: new Select<Group>(),
		skillBuilder: new Select<SkillBuilder>(),
		...CHARTS.reduce<{
			[key in Chart]?: {
				group: Select<Group>;
				viewAll: Select<boolean | null>;
			};
		}>((previousValue, currentValue) => {
			previousValue[currentValue] = {
				group: new Select<Group>(),
				viewAll: new Select<boolean | null>(SelectKey.VIEW_ALL),
			};
			return previousValue;
		}, {}),
	};

	// Chart configurations.
	chartConfigurations = CHARTS.reduce<{
		[key in Chart]?: ReplaySubject<ChartConfiguration>;
	}>((previousValue, currentValue) => {
		previousValue[currentValue] = new ReplaySubject<ChartConfiguration>(1);
		return previousValue;
	}, {});

	// Local data storage.
	users: ExposedPromise<Array<User> | null> =
		new ExposedPromise<Array<User> | null>();
	stats: ExposedPromise<LeaderChallengeStatsAPIResponse | null> =
		new ExposedPromise<LeaderChallengeStatsAPIResponse | null>();

	// Organization Stats.
	organizationStats: any = {
		completed: 0,
		streak: 0,
		positive: 0,
		goal: 0,
	};

	// Skill Builder Engagement.
	skillBuilderEngagementPage: number = 1;
	skillBuilderEngagementData: Array<any> = [];

	// Duplicate call prevention.
	private _loadCall: {
		stats: Promise<void> | null;
		skill: Promise<void> | null;
		activity: Promise<void> | null;
		goal: Promise<void> | null;
		impact: Promise<void> | null;
		engagement: Promise<void> | null;
	} = {
		stats: null,
		skill: null,
		activity: null,
		goal: null,
		impact: null,
		engagement: null,
	};

	// Subscription tracker.
	private _subscriptions: Subscription = new Subscription();

	// Page language.
	page: { [key: string]: string } = {};

	constructor(
		private _groupSvc: GroupService,
		private _languageSvc: LanguageService,
		private _organizationSvc: OrganizationService,
		private _leaderSkillBuilderSvc: LeaderSkillBuilderService,
		private _skillBuilderSvc: SkillBuilderService,
		private _storageSvc: StorageService,
		private _userSvc: LeaderUserService,
	) {}

	ngOnInit(): void {
		this._languageSvc.get([PAGE_PATH]).then((value) => {
			if (typeof value[PAGE_PATH] === 'object' && value[PAGE_PATH] !== null)
				this.page = value[PAGE_PATH];
		});

		// Set filter events.
		this._subscriptions.add(
			this.filters.organization.onChange.subscribe(
				(organization: Organization) => {
					this._storageSvc.setMainStorage(
						MainKey.SELECTED_ORGANIZATION_UUID,
						organization.uuid,
					);
					this._loadCall.stats = this._loadCall.stats ?? this._loadStats();
					this._loadCall.engagement =
						this._loadCall.engagement ?? this._loadEngagement();
					for (const chart of CHARTS)
						this._loadCall[chart] =
							this._loadCall[chart] ?? this._loadChart(chart);
				},
			),
		);
		this._subscriptions.add(
			this.filters.group.onChange.subscribe(
				() =>
					(this._loadCall.engagement =
						this._loadCall.engagement ?? this._loadEngagement()),
			),
		);
		this._subscriptions.add(
			this.filters.skillBuilder.onChange.subscribe(
				() =>
					(this._loadCall.engagement =
						this._loadCall.engagement ?? this._loadEngagement()),
			),
		);
		for (const chart of CHARTS) {
			this._subscriptions.add(
				this.filters[chart].group.onChange.subscribe(
					() =>
						(this._loadCall[chart] =
							this._loadCall[chart] ?? this._loadChart(chart)),
				),
			);
			this._subscriptions.add(
				this.filters[chart].viewAll.onChange.subscribe(
					() =>
						(this._loadCall[chart] =
							this._loadCall[chart] ?? this._loadChart(chart)),
				),
			);
		}

		// Initialize filters.
		this._groupSvc
			.getGroups(
				true,
				(group: Group) => group.role !== null && group.memberUUIDs.length > 0,
			)
			.then(async (value: Array<Group>) => {
				this._subscriptions.add(
					this.filters.organization.onChange.subscribe(
						(organization: Organization) => {
							if (!organization || organization.uuid === null) {
								this.filters.group.options = value;
								CHARTS.forEach(
									(chart) => (this.filters[chart].group.options = value),
								);
							} else {
								const groups = value.filter(
									(group: Group) =>
										group.uuid === null ||
										group.organizationUUID === organization.uuid,
								);
								this.filters.group.options = groups;
								CHARTS.forEach(
									(chart) => (this.filters[chart].group.options = groups),
								);
							}
						},
					),
				);
				this.filters.group.loaded = true;
				CHARTS.forEach((chart) => (this.filters[chart].group.loaded = true));
			});
		this._organizationSvc
			.getOrganizations((organization: Organization) => organization.leader)
			.then(async (value: Array<Organization>) => {
				await this.filters.group.loaded;
				this.filters.organization.options = value;
				this.filters.organization.value =
					this.filters.organization.options.find(
						(organization: Organization) =>
							organization.uuid ===
							this._storageSvc.getMainStorage(
								MainKey.SELECTED_ORGANIZATION_UUID,
							),
					) || this.filters.organization.value;
				this.filters.organization.loaded = true;
			});
		CHARTS.forEach((chart) => {
			this.filters[chart].viewAll.options = [null, true, false];
			this.filters[chart].viewAll.loaded = true;
		});
		this.filters.skillBuilder.options = [
			ALL_SKILL_BUILDERS,
			...this._skillBuilderSvc.getSkillBuilders(SKILL_BUILDER_FILTERS.REPORT),
		];
		this.filters.skillBuilder.loaded = true;

		// Load user data.
		this._userSvc.getUsers().subscribe((users) => {
			this.users.resolve(users);
		});

		// Get skill builder stats.
		this._leaderSkillBuilderSvc
			.getStats()
			.subscribe((res: LeaderChallengeStatsAPIResponse) => {
				this.stats.resolve(res);
			});
	}

	objectToArray<T extends {}, K extends keyof T>(obj: T): Array<T[K]> {
		return Object.values(obj);
	}

	private async _loadStats(): Promise<void> {
		// Load all necessary data and clear the call.
		let stats: LeaderChallengeStatsAPIResponse;
		await Promise.all([
			this.filters.organization.loaded,
			this.stats.then((value) => (stats = value)),
		]);
		this._loadCall.stats = null;

		// Get current organization stats.
		this.organizationStats = stats.organizations.find(
			(organization) =>
				organization.uuid === this.filters.organization.value.uuid,
		) || {
			completed: 0,
			streak: 0,
			positive: 0,
			goal: 0,
		};
		this.organizationStats.streak = Math.round(this.organizationStats.streak);
		this.organizationStats.goal = Math.round(this.organizationStats.goal);
	}

	private async _loadEngagement(): Promise<void> {
		// Load all necessary data and clear the call.
		let stats: LeaderChallengeStatsAPIResponse;
		let users: Array<User>;
		await Promise.all([
			this.filters.organization.loaded,
			this.filters.group.loaded,
			this.filters.skillBuilder.loaded,
			this.stats.then((value) => (stats = value)),
			this.users.then((value) => (users = value)),
		]);
		this._loadCall.engagement = null;

		this.skillBuilderEngagementPage = 1;
		this.skillBuilderEngagementData = [];
		const allUsers: Array<uuid> = [];
		for (const user of stats.users) {
			// Limit by organization.
			if (user.organizationUUID !== this.filters.organization.value.uuid)
				continue;

			// Limit by group.
			if (
				!!this.filters.group.value.uuid &&
				user.groupUUID !== this.filters.group.value.uuid
			)
				continue;

			// Prevent duplicate user entries.
			if (allUsers.includes(user.uuid)) continue;
			else allUsers.push(user.uuid);

			// Get the user name if available.
			const userName =
				users.find((userData) => userData.uuid === user.uuid)?.name ??
				user.genericName;

			// Build the table records.
			if (this.filters.skillBuilder.value.type) {
				if (user[`${this.filters.skillBuilder.value.type}Completed`])
					this.skillBuilderEngagementData.push({
						uuid: user.uuid,
						name: userName,
						skillBuilder: this.filters.skillBuilder.value.name,
						completed: user[`${this.filters.skillBuilder.value.type}Completed`],
					});
			} else {
				for (const skillBuilder of this.filters.skillBuilder.options) {
					if (!skillBuilder.type) continue;
					if (user[`${skillBuilder.type}Completed`])
						this.skillBuilderEngagementData.push({
							uuid: user.uuid,
							name: userName,
							skillBuilder: skillBuilder.name,
							completed: user[`${skillBuilder.type}Completed`],
						});
				}
			}
		}

		// Order by name and skill builder.
		this.skillBuilderEngagementData.sort((a, b) => {
			if (a.name < b.name) return -1;
			if (a.name > b.name) return 1;
			if (a.skillBuilder < b.skillBuilder) return -1;
			if (a.skillBuilder > b.skillBuilder) return 1;
			return 0;
		});
	}

	private get EMPTY_CHART_CONFIGURATION(): ChartConfiguration {
		return {
			type: 'bar',
			data: {
				datasets: [
					{
						label: '',
						data: [],
						borderRadius: 5,
					},
				],
			},
			options: {
				plugins: {
					legend: {
						display: false,
						position: 'top',
						align: 'end',
						labels: {
							useBorderRadius: true,
							borderRadius: 7.5,
							boxWidth: 15,
							boxHeight: 15,
						},
					},
				},
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y',
				scales: {
					y: {
						grid: {
							display: false,
						},
					},
					x: {
						min: 0,
						ticks: {
							stepSize: 10,
						},
						title: {
							display: true,
							color: '#757575',
						},
					},
				},
			},
		};
	}

	async _loadChart(chart: Chart): Promise<void> {
		// Load all necessary data and clear the call.
		let stats: LeaderChallengeStatsAPIResponse;
		let users: Array<User>;
		await Promise.all([
			this.filters.organization.loaded,
			this.filters[chart].group.loaded,
			this.filters[chart].viewAll.loaded,
			this.stats.then((value) => (stats = value)),
			this.users.then((value) => (users = value)),
		]);
		this._loadCall[chart] = null;

		switch (chart) {
			case 'skill':
				this._loadSkill(stats, users);
				break;
			case 'activity':
				this._loadActivity(stats, users);
				break;
			case 'goal':
				this._loadGoal(stats, users);
				break;
			case 'impact':
				this._loadImpact(stats, users);
				break;
		}
	}

	private _loadSkill(
		stats: LeaderChallengeStatsAPIResponse,
		users: Array<User>,
	): void {
		// Compile chart data.
		const chartData: Array<{ label: string; score: number }> = [];
		if (this.filters.skill.group.value.uuid) {
			for (const user of stats.users) {
				if (user.groupUUID !== this.filters.skill.group.value.uuid) continue;
				const userData = users.find((userData) => userData.uuid === user.uuid);
				if (!!userData)
					chartData.push({
						label: userData.name,
						score: user.completed,
					});
			}
		} else {
			const groups = this.filters.skill.group.options.map((group) => {
				return { uuid: group.uuid, name: group.name, score: 0 };
			});
			const allGroups = groups.find((group) => group.uuid === null);
			const allUsers: Array<uuid> = [];
			for (const user of stats.users) {
				const group = groups.find((group) => group.uuid === user.groupUUID);
				if (!!group) {
					group.score += user.completed;
					if (!allUsers.includes(user.uuid)) {
						allGroups.score += user.completed;
						allUsers.push(user.uuid);
					}
				}
			}
			for (const group of groups)
				chartData.push({ label: group.name, score: group.score });
		}

		// Check if viewAll needs to be toggled.
		if (this.filters.skill.viewAll.value === null && chartData.length > 6) {
			this.filters.skill.viewAll.value = false;
			return;
		}
		if (this.filters.skill.viewAll.value === false && chartData.length <= 6) {
			this.filters.skill.viewAll.value = null;
			return;
		}

		// Sort by descending score.
		chartData.sort((a, b) => b.score - a.score);

		// Build configuration.
		const configuration: ChartConfiguration = this.EMPTY_CHART_CONFIGURATION;
		if (this.filters.skill.viewAll.value === false)
			chartData.splice(6, Infinity);
		configuration.data.labels = chartData.map((data) => data.label);
		configuration.data.datasets[0].data = chartData.map((data) => data.score);
		configuration.data.datasets[0].backgroundColor = '#74A2FF';
		configuration.options.scales.x.max = Math.max(
			...chartData.map((data) => data.score),
			100,
		);
		(<any>configuration.options.scales.x).title.text =
			this.page?.numSkillBuilders;
		this.chartConfigurations.skill.next(configuration);
	}

	private _loadActivity(
		stats: LeaderChallengeStatsAPIResponse,
		users: Array<User>,
	): void {
		// Compile chart data.
		const chartData: Array<{ label: string; score: number }> = [];
		if (this.filters.activity.group.value.uuid) {
			for (const user of stats.users) {
				if (user.groupUUID !== this.filters.activity.group.value.uuid) continue;
				const userData = users.find((userData) => userData.uuid === user.uuid);
				if (!!userData)
					chartData.push({
						label: userData.name,
						score: user.streak,
					});
			}
		} else {
			const groups = this.filters.activity.group.options.map((group) => {
				return { uuid: group.uuid, name: group.name, scores: [] };
			});
			const allGroups = groups.find((group) => group.uuid === null);
			const allUsers: Array<uuid> = [];
			for (const user of stats.users) {
				if (user.completed === 0) continue;
				const group = groups.find((group) => group.uuid === user.groupUUID);
				if (!!group) {
					group.scores.push(user.streak);
					if (!allUsers.includes(user.uuid)) {
						allGroups.scores.push(user.streak);
						allUsers.push(user.uuid);
					}
				}
			}
			for (const group of groups) {
				let score = 0;
				if (group.scores.length > 0)
					score = Math.round(
						group.scores.reduce((a, b) => a + b, 0) / group.scores.length,
					);
				chartData.push({ label: group.name, score });
			}
		}

		// Check if viewAll needs to be toggled.
		if (this.filters.activity.viewAll.value === null && chartData.length > 6) {
			this.filters.activity.viewAll.value = false;
			return;
		}
		if (
			this.filters.activity.viewAll.value === false &&
			chartData.length <= 6
		) {
			this.filters.activity.viewAll.value = null;
			return;
		}

		// Sort by descending score.
		chartData.sort((a, b) => b.score - a.score);

		// Build configuration.
		const configuration: ChartConfiguration = this.EMPTY_CHART_CONFIGURATION;
		if (this.filters.activity.viewAll.value === false)
			chartData.splice(6, Infinity);
		configuration.data.labels = chartData.map((data) => data.label);
		configuration.data.datasets[0].data = chartData.map((data) => data.score);
		configuration.data.datasets[0].backgroundColor = '#3CE986';
		configuration.options.scales.x.max = Math.max(
			...chartData.map((data) => data.score),
			100,
		);
		(<any>configuration.options.scales.x).title.text = this.page?.avgStreak;
		this.chartConfigurations.activity.next(configuration);
	}

	private _loadGoal(
		stats: LeaderChallengeStatsAPIResponse,
		users: Array<User>,
	): void {
		// Compile chart data.
		const chartData: Array<{ label: string; score: number }> = [];
		if (this.filters.goal.group.value.uuid) {
			for (const user of stats.users) {
				if (user.groupUUID !== this.filters.goal.group.value.uuid) continue;
				const userData = users.find((userData) => userData.uuid === user.uuid);
				if (!!userData)
					chartData.push({
						label: userData.name,
						score: Math.round(user.goal),
					});
			}
		} else {
			const groups = this.filters.goal.group.options.map((group) => {
				return { uuid: group.uuid, name: group.name, scores: [] };
			});
			const allGroups = groups.find((group) => group.uuid === null);
			const allUsers: Array<uuid> = [];
			for (const user of stats.users) {
				if (user.completed === 0) continue;
				const group = groups.find((group) => group.uuid === user.groupUUID);
				if (!!group) {
					group.scores.push(user.goal);
					if (!allUsers.includes(user.uuid)) {
						allGroups.scores.push(user.goal);
						allUsers.push(user.uuid);
					}
				}
			}
			for (const group of groups) {
				let score = 0;
				if (group.scores.length > 0)
					score = Math.round(
						group.scores.reduce((a, b) => a + b, 0) / group.scores.length,
					);
				chartData.push({ label: group.name, score });
			}
		}

		// Check if viewAll needs to be toggled.
		if (this.filters.goal.viewAll.value === null && chartData.length > 6) {
			this.filters.goal.viewAll.value = false;
			return;
		}
		if (this.filters.goal.viewAll.value === false && chartData.length <= 6) {
			this.filters.goal.viewAll.value = null;
			return;
		}

		// Sort by descending score.
		chartData.sort((a, b) => b.score - a.score);

		// Build configuration.
		const configuration: ChartConfiguration = this.EMPTY_CHART_CONFIGURATION;
		if (this.filters.goal.viewAll.value === false)
			chartData.splice(6, Infinity);
		configuration.data.labels = chartData.map((data) => data.label);
		configuration.data.datasets[0].data = chartData.map((data) => data.score);
		configuration.data.datasets[0].backgroundColor = '#3CE986';
		configuration.options.scales.x.max = Math.max(
			...chartData.map((data) => data.score),
			100,
		);
		(<any>configuration.options.scales.x).title.text = this.page?.percentTime;
		this.chartConfigurations.goal.next(configuration);
	}

	private _loadImpact(
		stats: LeaderChallengeStatsAPIResponse,
		users: Array<User>,
	): void {
		// Compile chart data.
		const chartData: Array<{ label: string; score: number }> = [];
		if (this.filters.impact.group.value.uuid) {
			for (const user of stats.users) {
				if (user.groupUUID !== this.filters.impact.group.value.uuid) continue;
				const userData = users.find((userData) => userData.uuid === user.uuid);
				if (!!userData)
					chartData.push({
						label: userData.name,
						score: user.positive,
					});
			}
		} else {
			const groups = this.filters.impact.group.options.map((group) => {
				return { uuid: group.uuid, name: group.name, score: 0 };
			});
			const allGroups = groups.find((group) => group.uuid === null);
			const allUsers: Array<uuid> = [];
			for (const user of stats.users) {
				const group = groups.find((group) => group.uuid === user.groupUUID);
				if (!!group) {
					group.score += user.positive;
					if (!allUsers.includes(user.uuid)) {
						allGroups.score += user.positive;
						allUsers.push(user.uuid);
					}
				}
			}
			for (const group of groups)
				chartData.push({ label: group.name, score: group.score });
		}

		// Check if viewAll needs to be toggled.
		if (this.filters.impact.viewAll.value === null && chartData.length > 6) {
			this.filters.impact.viewAll.value = false;
			return;
		}
		if (this.filters.impact.viewAll.value === false && chartData.length <= 6) {
			this.filters.impact.viewAll.value = null;
			return;
		}

		// Sort by descending score.
		chartData.sort((a, b) => b.score - a.score);

		// Build configuration.
		const configuration: ChartConfiguration = this.EMPTY_CHART_CONFIGURATION;
		if (this.filters.impact.viewAll.value === false)
			chartData.splice(6, Infinity);
		configuration.data.labels = chartData.map((data) => data.label);
		configuration.data.datasets[0].data = chartData.map((data) => data.score);
		configuration.data.datasets[0].backgroundColor = '#74A2FF';
		configuration.options.scales.x.max = Math.max(
			...chartData.map((data) => data.score),
			100,
		);
		(<any>configuration.options.scales.x).title.text = this.page?.numInstances;
		this.chartConfigurations.impact.next(configuration);
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}
}
